import { useQuery } from "react-query";
import getOnboardingStatus from "../services/getOnboardingStatus";
import { OnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";

/**
 * Configuration constants for onboarding status polling
 */
const THIRTY_SECONDS_MS = 30 * 1000; // 30 seconds in milliseconds
const TWENTY_SECONDS_MS = 20 * 1000; // Stale time (data fresh for 20s)

/**
 * Final onboarding statuses that don't require polling
 * Once a status reaches these states, further polling is unnecessary
 */
const FINAL_STATUSES = [
  OnboardingStatusEnum.APPROVED,
  OnboardingStatusEnum.REJECTED,
];

interface UseGetOnboardingStatusParams {
  userId: number | null;
  token: string;
  enabled?: boolean;
}

/**
 * Custom hook to fetch and poll onboarding status
 *
 * Features:
 * - Automatic polling every 30 seconds
 * - Stops polling when status reaches final state (approved/rejected/expired)
 * - Refetches on window focus
 * - Caches results to reduce unnecessary requests
 *
 * @param userId - The merchant/user ID to fetch status for
 * @param token - Authentication token
 * @param enabled - Whether the query should run (default: true if userId exists)
 * @returns React Query result with status data, loading state, and error
 */
function useGetOnboardingStatus({
  userId,
  token,
  enabled = true,
}: UseGetOnboardingStatusParams) {
  const queryResult = useQuery(
    ["onboarding-status", userId],
    () => getOnboardingStatus({ userId: userId!, token }),
    {
      // Only run query if we have a userId, token, and enabled is true
      enabled: enabled && !!userId && !!token,

      // Poll every 30 seconds
      refetchInterval: (data) => {
        // Stop polling if status is final
        if (data && FINAL_STATUSES.includes(data.status)) {
          return false; // Stops polling
        }
        return THIRTY_SECONDS_MS;
      },

      // Refetch when user returns to tab
      refetchOnWindowFocus: true,

      // Data is fresh for 20 seconds (avoids redundant fetches)
      staleTime: TWENTY_SECONDS_MS,

      // Keep previous data while refetching (prevents UI flicker)
      keepPreviousData: true,

      // Retry once on failure, then stop
      retry: 1,
      retryDelay: 5000, // Wait 5 seconds before retry

      // Log errors but don't throw (prevents crashes)
      onError: (error) => {
        console.warn("Background onboarding status poll failed:", error);
      },
    }
  );

  return queryResult;
}

export default useGetOnboardingStatus;
